// Context: the strings marked "placeholder" are owed by Lammert (terminology owner); each ships
// as written until his wording arrives, and none of them may say something false on a Draft or an
// Approved bill (DL-40).
export default {
  // The two routes into the drawer. The menu item is title case like its siblings and the phone
  // card sentence case; they are separate keys so either can change on its own (DL-93).
  entry: {
    siteActions: 'Provisional Budgets',
    card: 'Provisional budgets',
    // Placeholder: the card's accessible name by its count (zero, one, more), from the design's
    // entry surface; the singular form is ours.
    cardAwaiting:
      'Provisional budgets, nothing awaiting a decision | Provisional budgets, {count} awaiting a decision | Provisional budgets, {count} awaiting a decision',
    // Placeholder: the card's accessible name while its count is loading or could not be read.
    cardCountUnknown: 'Provisional budgets',
  },
  drawer: {
    title: 'Provisional budgets',
    siteContext: '{siteName} · {contract}',
    // Placeholder: the design's "Site Contract" suffix.
    siteContract: '{contractType} Site Contract',
    create: 'Create',
    backToRoot: 'Back to provisional budgets',
    retry: 'Try again',
    // Placeholder: the three refusal messages.
    errors: {
      refused: 'You do not have access to provisional budgets on this bill.',
      absent: 'Provisional budgets are not available on this bill.',
      failed: 'Provisional budgets could not be loaded.',
    },
    // Placeholder: the one line under the title row of a read-only drawer, keyed by the regime's
    // reason (DL-45). It speaks for the whole drawer, so it says something different from the
    // bodies' `decision.cannotDecide.*` and `create.readOnly.*` pairs; Lammert words the three
    // pairs together.
    readOnly: {
      ArchivedSite:
        'This site is archived, so its provisional budgets can be read but not changed.',
      FlagOff: 'Provisional budgets on this bill can be read but not changed at the moment.',
    },
  },
  // Placeholder: a pending create's reserve is not in the BOQ total until it is approved (DL-40). One
  // key for the root row, the record and the Pending tab's Create row; Lammert words it in one row
  // with `root.barValueTextPending`, which says the same thing to a screen reader (DL-158).
  notInBoqTotal: 'Not yet in the BOQ total',
  // The legend under the split bar, one set for the root's band and the record's Details card so
  // the two read alike.
  splitLegend: {
    allocated: 'Allocated',
    // Placeholder: it counts allocation money awaiting a decision, which a pending create is not
    // (DL-40), so it says so rather than reading R 0.00 beside a pending request.
    awaiting: 'Allocations awaiting a decision',
    unallocated: 'Unallocated',
  },
  root: {
    band: {
      unallocated: 'Unallocated',
      ofTotal: 'of Total',
      // Placeholder.
      barLabel: 'Allocated, awaiting a decision and unallocated reserve on this bill',
    },
    columns: {
      name: 'Provisional budget',
      split: 'Split',
      unallocated: 'Unallocated',
      total: 'Total',
      utilisation: 'Util.',
    },
    // Placeholder.
    rowAwaiting: '{name}, {count} awaiting a decision',
    // Placeholder: a row's accessible name when its reserve is not yet in the BOQ total. The name
    // replaces the row's content for a screen reader, so it carries the visible label, which is
    // hidden from assistive tech; worded with `notInBoqTotal` (DL-158).
    rowNotInBoqTotal: '{row}. {notInBoqTotal}',
    // Placeholder.
    splitLabel: 'Allocated, awaiting a decision and unallocated reserve of {name}',
    // Placeholder: a pending create's bar holds only a requested reserve, with nothing unallocated.
    splitLabelPending: 'Requested reserve of {name}',
    // Placeholder: what both bars announce in place of a percentage, in the legend's words
    // (`splitLegend`), so a screen reader hears what a sighted reader reads.
    barValueText:
      'Allocated {allocated}, allocations awaiting a decision {awaiting}, unallocated {unallocated}',
    // Placeholder: what a pending create's bar announces, since its reserve is not yet in the BOQ
    // total and has no unallocated money to announce (DL-40).
    barValueTextPending: 'Requested {requested}, not yet in the BOQ total',
    empty: {
      title: 'Nothing is set aside on this bill',
      // Placeholder: the empty-state body (DL-40).
      description: 'Set money aside on this bill for work that is not yet specified.',
      create: 'Create provisional budget',
    },
    // Placeholder: the announcement after a create, one per outcome the server reports. A pending
    // create must say it awaits a decision and is not yet in the BOQ total (PE-918).
    announcements: {
      Created: '{name} was added to this bill.',
      Requested: '{name} was created and is awaiting a decision. It is not yet in the BOQ total.',
      ApprovedByRaiser: '{name} was approved and added to this bill.',
    },
  },
  record: {
    // Placeholder: the four status labels (DL-89).
    status: {
      Pending: 'Awaiting a decision',
      Open: 'Open',
      FullyAllocated: 'Fully allocated',
      Closed: 'Closed out',
    },
    tabs: {
      details: 'Details',
      pending: 'Pending',
    },
    retry: 'Try again',
    // Placeholder: the record's refusal messages.
    errors: {
      refused: 'You do not have access to this provisional budget.',
      absent: 'This provisional budget is not available.',
      failed: 'This provisional budget could not be loaded.',
    },
    details: {
      unallocated: 'Unallocated',
      ofTotal: 'of {total} total',
      // Placeholder: said only while the original equals the current reserve (DL-61). M3's moved
      // form is "of {total} total · {original} at the start".
      ofTotalUnchanged: 'of {total} total, unchanged since it was set',
      utilisation: 'Utilisation',
      ofOriginal: 'of original',
      // Placeholder.
      barLabel: 'Allocated, awaiting a decision and unallocated reserve of this provisional budget',
      // Placeholder: a pending create's money card holds only the requested reserve (DL-40).
      requestedReserve: 'Requested reserve',
      description: 'Description',
      // Placeholder: "Created", not "Raised", because a Draft create was never raised; it matches
      // the design's history line.
      createdBy: 'Created by {name} on {date}',
      // Placeholder: a creator with no recorded time.
      createdByUndated: 'Created by {name}',
      createdByNotRecorded: 'Created by not recorded',
      noDescription:
        'No description recorded. Nobody wrote down what this reserve covers or what risk it carries, so the record cannot say.',
      // Placeholder: the design's heading for the sections card.
      sections: 'Allocate only to these sections',
      anySection: 'Any section on this bill',
      // Placeholder for Lammert: a restricted section the bill the drawer holds does not list.
      unknownSection: 'A section not shown here',
    },
    pending: {
      columns: {
        date: 'Date',
        request: 'Request',
        valueChange: 'Value change',
        action: 'Action',
      },
      kinds: {
        Create: 'Create',
      },
      titles: {
        Create: 'Create provisional budget',
      },
      // Placeholder: a request kind M1 cannot raise.
      unknownKind: 'Request',
      approve: 'Approve',
      decline: 'Decline',
      // Placeholder: the card list's date line, at full precision because touch has no tooltip.
      raisedOn: 'Raised {date}',
      // Placeholder: a request whose raised time could not be read.
      dateNotRecorded: 'Date not recorded',
      // Placeholder: who to ask, keyed by approver so a later kind adds an entry rather than a
      // branch.
      withApprover: 'With {approvers}',
      // Placeholder: joins two approvers, a case no M1 kind has.
      approverJoin: ' or ',
      approvers: {
        procurementOfficer: 'the Procurement Officer',
        // Placeholder: a role no M1 kind routes to.
        unknown: 'the approver',
      },
      // Placeholder: nobody on the site holds a role that may decide the request (DL-63, DL-85).
      noApprover: 'This request cannot be approved here: nobody on this site can decide it.',
      empty: {
        title: 'Nothing awaits a decision',
        description: 'Requests that need a decision appear here.',
      },
    },
  },
  decision: {
    // The precise SAST time, inline at every width, so it reads the same on touch.
    requested: 'Requested {date} by {name}',
    // Placeholder: a request whose raised time could not be read.
    requestedUndated: 'Requested by {name} · {dateNotRecorded}',
    quotedReason: '“{reason}”',
    noReason: 'No reason was given with this request.',
    // Why this reader cannot decide, keyed by cause; nobody on the site able to decide reads
    // `record.pending.noApprover`, the Pending row's own wording (DL-85).
    cannotDecide: {
      ReadOnly: 'You are reading this request. Deciding it needs rights you do not hold.',
      // Placeholder: the bill is read-only because its site is archived (DL-45).
      ArchivedSite: 'This site is archived, so this request cannot be decided.',
      // Placeholder: the bill is read-only because the feature is off.
      FlagOff:
        'Provisional budgets cannot be changed at the moment, so this request cannot be decided.',
    },
    failures: {
      // Placeholder: the decision may have landed, so it must not say that nothing has changed.
      Unconfirmed:
        'We could not confirm this decision. Try again; if it already went through, nothing more will happen.',
      // Placeholder: a 401; the session was renewed and the decision was not sent again (#2566).
      SessionRenewed: 'Your session was renewed and nothing was decided. Send it again.',
      // Placeholder.
      NotAwaiting:
        'This request could not be decided and nothing has changed: it is no longer awaiting a decision.',
      // Placeholder.
      Refused: 'This request could not be decided and nothing has changed.',
      Invalid: 'This request could not be decided and nothing has changed. Try again.',
      // Placeholder.
      Forbidden:
        'This request could not be decided and nothing has changed: deciding it needs rights you do not hold.',
      // Placeholder.
      Gone: 'This request is no longer available.',
      // Placeholder: a 409 whose fresh read still lists the request, as after a lost race with
      // another decision on the same bill.
      staleStillAwaiting: 'This request was changed at the same time. Check it and try again.',
      // Placeholder: a 409 whose fresh read no longer lists the request.
      staleNoLongerAwaiting:
        'This request was changed at the same time, and it is no longer awaiting a decision.',
    },
    createApprove: {
      title: 'Approve this new provisional budget?',
      // Placeholder for Lammert: the title for a reader who cannot decide, a statement not a
      // question, since nothing is asked of them.
      titleReadOnly: 'This new provisional budget awaits approval',
      figure: 'Opening reserve',
      commit: 'Approve',
      done: 'Approved',
      toDecline: 'Decline',
    },
    createDecline: {
      title: 'Decline this new provisional budget?',
      figure: 'Opening reserve refused',
      commit: 'Decline',
      done: 'Declined',
      reasonLabel: 'Reason for declining',
      reasonPlaceholder: 'State why this is being declined. The requester sees this.',
    },
    // Placeholder: the one polite announcement once a decision lands, made after its dialog has
    // gone, as a create's is (DL-71). Keyed on what the decision came to: a declined create removes
    // its budget, so it says so; a decline that keeps the budget (M3's value changes) does not.
    announcements: {
      Approved: '{name} was approved.',
      Declined: '{name} was declined.',
      DeclinedAndRemoved: '{name} was declined and removed.',
    },
    // The restriction a create request carries, decided with it (DL-256).
    sections: {
      none: 'Any section. The reserve is not restricted.',
      // Placeholder for Lammert.
      restricted: 'Allocate only to these sections:',
      // Placeholder for Lammert: a proposed section the bill the drawer holds does not list.
      unknown: 'A section not shown here',
    },
  },
  create: {
    // The one body whose title is not a question (§5.1 surface note).
    title: 'Create Provisional Budget',
    // Placeholder: read as part of the dialog's description on an Approved bill, never shown
    // (DL-71); the create raises a request there, so the money has not landed yet.
    approvedBillContext:
      'This raises a request. The reserve is not in the BOQ total until it is approved.',
    fields: {
      name: 'Name',
      description: 'Description',
      descriptionPlaceholder: 'What this reserve is for, and what is not yet known about it.',
      // The R prefix is drawn beside the field, so the label does not name the currency.
      amount: 'Amount',
      // Placeholder for Lammert: the currency a screen reader hears for the amount, since the
      // drawn R prefix is hidden from it and the label no longer names the currency.
      amountCurrency: 'In rand',
      reason: 'Reason',
      // Placeholder for Lammert: the design's label. An optional field carries no marker (DL-306).
      sections: 'Allocate only to these sections',
      // Placeholder for Lammert: the design's hint. Empty is the default and means unrestricted.
      sectionsHint: 'Leave this empty and any section may be allocated to.',
      // Placeholder for Lammert: the picker's value with nothing chosen.
      sectionsAny: 'Any section',
      // Placeholder for Lammert: the zero form is read only in `sectionsRemoved`.
      sectionsChosen: 'no section chosen | {count} section chosen | {count} sections chosen',
      // Placeholder for Lammert: said politely once a chip has removed its section.
      sectionsRemoved: '{name} removed, {chosen}',
      // Placeholder for Lammert: the accessible name of the chosen sections' chips.
      sectionsChosenList: 'Chosen sections',
      // Placeholder for Lammert.
      sectionsRemove: 'Remove {name}',
      // Placeholder for Lammert: the search box above the open list, and its accessible name.
      sectionsSearch: 'Search sections',
      // Placeholder for Lammert: a search that matches none of the bill's sections.
      sectionsNoMatch: 'No section matches',
      // Placeholder for Lammert: the hint at the foot of the open list.
      sectionsSelectHint: 'Choose one or more sections',
      // Placeholder for Lammert: a picked section left the bill before the submit, so nothing was
      // sent; the gone picks are taken off the field.
      sectionsGone:
        'A section you chose is no longer on this bill. Check the sections and submit again.',
      // Placeholder for Lammert: a pick whose name is not known while the bill is not held here.
      sectionsNotShown: 'A section not shown here',
      // Placeholder for Lammert: a bill with no sections, shown to a reader who may not add one, so
      // the picker would offer them nothing (DL-267).
      sectionsNone: 'This bill has no sections yet, so the reserve cannot be restricted to any.',
      // Placeholder for Lammert: the open list on a bill with no sections, for a reader who may add
      // one there (DL-267).
      sectionsEmpty: 'This bill has no sections yet. Type a name to create one.',
      // Placeholder for Lammert: the action offered below the list when no section is named exactly
      // what was typed, even when longer names match it (DL-269); the section is made with the
      // create, at submit (DL-260).
      sectionsCreate: "Create section '{name}'",
      // Placeholder for Lammert: said politely once the action is offered, since it appears below a
      // list the reader is typing above, which may also list sections.
      sectionsCreateOffered: "You can create section '{name}', below the list.",
      // Placeholder for Lammert: in place of the action when what was typed is too long to name a
      // section (the server's limit).
      sectionsNameTooLong: 'A section name can be at most {max} characters.',
      // Placeholder for Lammert: a section the create will make, as its chip's remove control and its
      // announcements name it.
      sectionsNew: '{name} (new)',
      // Placeholder for Lammert: the label beside a new section's name on its chip, shown in capitals.
      sectionsNewLabel: 'New',
      // Placeholder for Lammert: said politely once a new section is chosen, as `sectionsRemoved`.
      sectionsAdded: '{name} added, {chosen}',
    },
    commit: {
      submit: 'Submit',
      request: 'Request',
    },
    done: {
      created: 'Created',
      sent: 'Sent',
    },
    // Placeholder: in place of the modal's footer while the bill is read-only, keyed by the regime's
    // reason; worded with `drawer.readOnly.*`.
    readOnly: {
      ArchivedSite: 'This site is archived, so a provisional budget cannot be created on it.',
      FlagOff:
        'Provisional budgets cannot be changed at the moment, so this one cannot be created.',
    },
    failures: {
      // Placeholder.
      Invalid: 'This provisional budget could not be created. Check the details and try again.',
      // Placeholder: a validation refusal naming only a field this form does not show, as when the
      // bill turned Approved after the form opened as a Draft one, and the bill's status could not
      // be read again. The page holds the bill it loaded, so only a reload brings the right form.
      BillChanged:
        "This bill's status has changed since you opened this, so nothing was created. Reload the page to create the provisional budget.",
      // Placeholder: a 401; the session was renewed and the create was not sent again.
      SessionRenewed: 'Your session was renewed and nothing was created. Send it again.',
      // Placeholder: a 409 wrote nothing, so the same values can be sent again.
      Conflict:
        'This bill changed while the provisional budget was being created, so nothing was created. Try again.',
      // Placeholder: a 409, or a Reason refusal, on a form opened on a Draft bill that has since been
      // approved. Nothing was written, and the form now shows the Reason field and the Request commit.
      BillApproved:
        'This bill has been approved since you opened this, so nothing was created. Add a reason and request it.',
      // Placeholder.
      BillStatus:
        "This bill's status has changed, so a provisional budget cannot be created on it now.",
      // Placeholder.
      Refused: 'This provisional budget could not be created, and nothing has changed.',
      // Placeholder.
      Forbidden:
        'This provisional budget could not be created: creating one needs rights you do not hold.',
      // Placeholder.
      Absent: 'Provisional budgets are not available on this bill, so nothing was created.',
      // Placeholder: no answer came back, and a fresh read lists a new budget of this name and
      // amount, which may be someone else's.
      unconfirmedListed:
        'We could not confirm this create, but a provisional budget with this name and amount is now listed on this bill.',
      // Placeholder: a 500, 502 or 503, whose request has ended, and a fresh read lists nothing new.
      unconfirmedNotListed:
        'We could not confirm this create, and the provisional budget is not listed on this bill. Try again.',
      // Placeholder: a 504 or no answer at all, so the request may still be running, and a fresh
      // read lists nothing new yet. Sending it again could create the budget twice.
      unconfirmedNotYet:
        'We could not confirm this create. It is not listed on this bill yet, but it may still go through. Close this, wait a moment, then open the provisional budgets again and check before you try again.',
      // Placeholder: no answer came back, and the bill could not be read to tell. Sending it again
      // could create the budget twice.
      unconfirmedUnknown:
        'We could not confirm this create. Close this and check the list before you try again, so the provisional budget is not created twice.',
    },
  },
  // The discard confirmation for every provisional-budget body that can be typed into (PE-918
  // shares it). The two buttons are the design's, a recorded deviation from `.claude/rules/app/forms.md`'s generic
  // keys, which say "leave" where a modal stays.
  discard: {
    // Placeholder.
    title: 'Discard what you typed?',
    // Placeholder.
    content: 'What you typed will be lost.',
    keepEditing: 'Keep editing',
    discardAndClose: 'Discard and close',
  },
};

// Validation messages, mounted under `forms.provisionalBudgets` with the app's other form messages
// (`.claude/rules/app/forms.md`).
export const provisionalBudgetsForms = {
  create: {
    name: {
      required: 'Name this provisional budget, so the bill says what the reserve is for.',
      // Placeholder: the field stops at the limit, so only a pasted value over it can reach this.
      max: 'Keep the name to {max} characters or fewer.',
    },
    description: {
      // Placeholder: as for the name.
      max: 'Keep the description to {max} characters or fewer.',
    },
    amount: {
      required:
        'Enter an amount. A provisional budget holds a reserve, so it cannot be created without one.',
      positive:
        'Enter an amount greater than R 0.00. A provisional budget holds a reserve, so a zero-value one has nothing to allocate against.',
      // Placeholder: the field shows two decimals, so only a value it was handed can reach this.
      cents: 'Enter the amount in rand and cents, with no more than two decimal places.',
    },
    reason: {
      // Placeholder.
      required: 'Give a reason. The Procurement Officer decides the request with it.',
      // Placeholder: as for the name.
      max: 'Keep the reason to {max} characters or fewer.',
    },
  },
  declineReason: {
    required: 'Give a reason. The requester is notified with it.',
    // Placeholder: the field stops at the limit, so only a pasted value over it can reach this.
    max: 'Keep the reason to {max} characters or fewer.',
  },
};
